import type { Cluster } from '@googlemaps/markerclusterer';

import type { Marker } from '@libs/maps';
import { MapTypeId } from '@libs/maps';

import { i18n } from '../localization/localizationSetup';

export const enum MarkerType {
  Destination = 'destination-marker',
  Hotel = 'hotel-marker',
  Location = 'location-marker',
  Activity = 'activity-marker',
  Golf = 'golf-marker',
  Flight = 'flight-marker',
  Ferry = 'ferry-marker',
  DetailsAccommodation = 'details-accommodation-marker',
  DetailsActivity = 'details-activity-marker',
  DetailsGolf = 'details-golf-marker',
}

const { t } = i18n.global;

export type MarkerFormatter =
  | Record<MarkerType, (marker: Marker) => HTMLDivElement>
  | Record<MarkerType, string>;

export function getMarkerFormattersHTML(
  mapTypeId: MapTypeId
): Record<MarkerType, (marker: Marker) => HTMLDivElement> {
  return {
    [MarkerType.Destination]: (marker): HTMLDivElement => {
      const div = document.createElement('div');

      div.className = 'trip-planner-destination-marker';

      div.id = `destination-${marker.data?.ingredientId}`;

      div.innerHTML = `
          <div class="map-marker-icon"></div>
          <div class="map-marker-content">
            <div class="map-marker-title">${marker.title}</div>
            <div class="map-marker-subtitle">${marker.subtitle}</div>
          </div>
          <div class="map-marker-arrow"></div>
      `;

      return div;
    },
    [MarkerType.Hotel]: (marker): HTMLDivElement => {
      const div = document.createElement('div');

      div.className = `trip-planner-hotel-marker ${marker.cssClass || ''}`;

      div.innerHTML = `
      <div class="map-marker-hotel-title">
        <span>${marker.data?.formattedPrice}</span>
      </div>
    `;

      return div;
    },
    [MarkerType.Location]: (): HTMLDivElement => {
      const div = document.createElement('div');

      div.className = 'trip-planner-location-marker';

      return div;
    },
    [MarkerType.Activity]: (marker): HTMLDivElement => {
      const div = document.createElement('div');

      div.className = `trip-planner-activity-marker ${marker.cssClass || ''}`;

      div.innerHTML = `
        <div class="activity-marker-icon"></div>
        <div class="map-marker-content">
          <div class="map-marker-title">
            <span>${marker.data?.formattedPrice}</span>
          </div>
        </div>
    `;

      return div;
    },
    [MarkerType.Golf]: (marker): HTMLDivElement => {
      const div = document.createElement('div');

      div.className = `trip-planner-activity-marker ${marker.cssClass || ''}`;

      div.innerHTML = `
        <div class="golf-marker-icon"></div>
        <div class="map-marker-content">
          <div class="map-marker-title">
            <span>${marker.data?.formattedPrice}</span>
          </div>
        </div>
      `;

      return div;
    },
    [MarkerType.Flight]: (): HTMLDivElement => {
      const div = document.createElement('div');

      div.className = 'trip-planner-flight-marker';

      return div;
    },
    [MarkerType.Ferry]: (): HTMLDivElement => {
      const div = document.createElement('div');

      div.className = 'trip-planner-ferry-marker';

      return div;
    },
    [MarkerType.DetailsAccommodation]: (marker): HTMLDivElement => {
      const div = document.createElement('div');

      div.className = `trip-planner-details-accommodation-marker ${
        marker.cssClass || ''
      }`;
      div.innerHTML = `<img src="${
        mapTypeId === MapTypeId.HYBRID
          ? 'https://cdn.sanity.io/images/inntox6v/production/542dfdb14801c9389bea94a1abbf7cd836404706-44x44.svg'
          : 'https://cdn.sanity.io/images/inntox6v/production/2a4aafbdfb33ae617ca6f7d965283fc313b66b53-44x44.svg'
      } "width="35" height="35" >`;
      return div;
    },
    [MarkerType.DetailsActivity]: (marker): HTMLDivElement => {
      const div = document.createElement('div');

      div.className = `trip-planner-details-activity-marker ${
        marker.cssClass || ''
      }`;
      div.innerHTML = `<img src="${
        mapTypeId === MapTypeId.HYBRID
          ? 'https://cdn.sanity.io/images/inntox6v/production/1285fb2af503c1b96858645ba13efed1d1d64c4b-44x44.svg'
          : 'https://cdn.sanity.io/images/inntox6v/production/e29a84b7eacf7ee98232dd2070f57572870c76d6-44x44.svg'
      }"width="35" height="35">`;
      return div;
    },
    [MarkerType.DetailsGolf]: (marker): HTMLDivElement => {
      const div = document.createElement('div');
      div.className = `trip-planner-details-golf-marker ${
        marker.cssClass || ''
      }`;
      div.innerHTML = `<img src="${
        mapTypeId === MapTypeId.HYBRID
          ? 'https://cdn.sanity.io/images/inntox6v/production/196485502946e83b641977e8e2421d16858b6739-44x44.svg'
          : 'https://cdn.sanity.io/images/inntox6v/production/3d4e65050bfd2f5cb77e80d014153708d93ecede-44x44.svg'
      }"width="35" height="35">`;
      return div;
    },
  };
}

export const clusterMarkerFormattersHTML: Record<
  MarkerType,
  (marker: Marker, cluster: Cluster) => HTMLDivElement
> = {
  [MarkerType.Destination]: () => document.createElement('div'),
  [MarkerType.Hotel]: (marker, cluster) => {
    const div = document.createElement('div');

    div.className = `trip-planner-hotel-marker ${marker.cssClass || ''}`;

    div.innerHTML = `
    <div class="map-marker-hotel-title">
      <span> ${cluster.count} ${t('planner.properties')} </span>
    </div>
  `;

    return div;
  },
  [MarkerType.Location]: () => document.createElement('div'),
  [MarkerType.Activity]: (marker, cluster) => {
    const div = document.createElement('div');

    div.className = `trip-planner-activity-marker ${marker.cssClass || ''}`;

    div.innerHTML = `
    <div class="map-marker-activity-title">
      <span> ${cluster.count} ${t('planner.activities')} </span>
    </div>
  `;

    return div;
  },
  [MarkerType.Golf]: (marker, cluster) => {
    const div = document.createElement('div');

    div.className = `trip-planner-golf-marker ${marker.cssClass || ''}`;

    div.innerHTML = `
    <div class="map-marker-golf-title">
      <span> ${cluster.count} ${t('planner.activities')} </span>
    </div>
  `;

    return div;
  },
  [MarkerType.Flight]: () => document.createElement('div'),
  [MarkerType.Ferry]: () => document.createElement('div'),
  [MarkerType.DetailsAccommodation]: () => document.createElement('div'),
  [MarkerType.DetailsActivity]: () => document.createElement('div'),
  [MarkerType.DetailsGolf]: () => document.createElement('div'),
};

// NOTE: Static activity url markers need to be of type PNG
export const markerFormattersURL: Partial<Record<MarkerType, string>> = {
  [MarkerType.DetailsAccommodation]:
    'https://cdn.sanity.io/images/inntox6v/production/9adf1615cf2476f216289306da10d2182ad993b1-44x44.png?w=35&h=35',
  [MarkerType.DetailsActivity]:
    'https://cdn.sanity.io/images/inntox6v/production/c3daf783c366188cbc0fec73609aac637a3aa9a6-44x44.png?w=35&h=35',
  [MarkerType.DetailsGolf]:
    'https://cdn.sanity.io/images/inntox6v/production/5fad91953fe09739b6d3540df8c64414a90ad901-44x44.png?w=35&h=35',
};
